import {
  STORE_META,
  STORE_QUERIES,
  idbDelete,
  idbGet,
  idbPut,
  idbValues,
  idbWipe,
} from './db';
import { tripMediaUrls } from '@travessa/shared';
import type { TripEnvelope, User } from '../types';

/**
 * What this device keeps for reading without a network.
 *
 * **A trip is kept once it is opened** — by any link: the owner's
 * `/app/trips/:id`, a public `/t/:handle/:slug`, a shared `/s/:token`. The same
 * link then opens it on a plane. What is kept is exactly what the page asked
 * the API for (`persist.ts` stores query results, not a parallel model), plus
 * the trip's photos and documents in the service worker's media cache.
 *
 * A private trip carries documents, booking references and door PINs, so the
 * other half of the promise matters as much: everything is wiped on logout,
 * the traveller can turn keeping off on this device («Настройки»), and the
 * settings list every trip held here with a way to drop it.
 *
 * Scope is **read-only**. Editing offline means queueing writes and resolving
 * conflicts on reconnect; the `/sync` contract is built for that, but it is a
 * separate piece of work, and a half-done version that loses an edit is worse
 * than an itinerary you can read on a plane.
 */

// ---------------------------------------------------------------------------
// The switch
// ---------------------------------------------------------------------------

const AUTO_KEY = 'travessa:offline-keep';

/** Whether opened trips are kept on this device. On unless turned off here. */
export function isKeepingOffline(): boolean {
  try {
    return localStorage.getItem(AUTO_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function setKeepingOffline(on: boolean): void {
  try {
    if (on) localStorage.removeItem(AUTO_KEY);
    else localStorage.setItem(AUTO_KEY, 'off');
  } catch {
    // Private mode without storage: the default stands.
  }
}

// ---------------------------------------------------------------------------
// Persisted queries
// ---------------------------------------------------------------------------

export interface PersistedQuery {
  /** TanStack's query hash — the store's key. */
  hash: string;
  key: readonly unknown[];
  data: unknown;
  updatedAt: number;
}

/** A trip envelope's key: `['trip', id]`, `['trip', 'shared', token]`, `['trip', 'public', handle, slug]`. */
export function isTripKey(key: readonly unknown[]): boolean {
  if (key[0] !== 'trip') return false;
  if (key[1] === 'shared') return key.length === 3;
  if (key[1] === 'public') return key.length === 4;
  return key.length === 2;
}

export function readPersistedQueries(): Promise<PersistedQuery[]> {
  return idbValues<PersistedQuery>(STORE_QUERIES);
}

export async function writePersistedQuery(entry: PersistedQuery): Promise<void> {
  await idbPut(STORE_QUERIES, entry.hash, entry);
}

export async function deletePersistedQuery(hash: string): Promise<void> {
  await idbDelete(STORE_QUERIES, hash);
}

// ---------------------------------------------------------------------------
// The signed-in user, so an owner's link opens without a network
// ---------------------------------------------------------------------------

export function readOfflineUser(): Promise<User | null> {
  return idbGet<{ value: User }>(STORE_META, 'user').then((entry) => entry?.value ?? null);
}

export async function saveOfflineUser(user: User): Promise<void> {
  if (!isKeepingOffline()) return;
  await idbPut(STORE_META, 'user', { value: user });
}

// ---------------------------------------------------------------------------
// Saved trips, for the settings screen
// ---------------------------------------------------------------------------

export interface MediaState {
  total: number;
  stored: number;
  savedAt: string;
}

export interface SavedTrip {
  hash: string;
  tripId: string;
  title: string;
  /** Where it was opened from: the link that opens it offline. */
  via: 'own' | 'shared' | 'public';
  savedAt: string;
  media: MediaState | null;
}

export function readMediaState(tripId: string): Promise<MediaState | null> {
  return idbGet<MediaState>(STORE_META, `media:${tripId}`);
}

export async function writeMediaState(tripId: string, state: MediaState): Promise<void> {
  await idbPut(STORE_META, `media:${tripId}`, state);
}

export async function listSavedTrips(): Promise<SavedTrip[]> {
  const entries = await readPersistedQueries();
  const trips = await Promise.all(
    entries
      .filter((entry) => isTripKey(entry.key))
      .map(async (entry) => {
        const envelope = entry.data as TripEnvelope | undefined;
        if (!envelope?.trip) return null;
        return {
          hash: entry.hash,
          tripId: envelope.trip.id,
          title: envelope.trip.title,
          via: entry.key[1] === 'shared' ? 'shared' : entry.key[1] === 'public' ? 'public' : 'own',
          savedAt: new Date(entry.updatedAt).toISOString(),
          media: await readMediaState(envelope.trip.id),
        } satisfies SavedTrip;
      }),
  );
  return trips
    .filter((trip): trip is SavedTrip => trip !== null)
    .sort((a, b) => b.savedAt.localeCompare(a.savedAt));
}

/**
 * Drop one trip from the device: its envelope, the queries hanging off it
 * (flight status), and its files — unless another saved link still shows the
 * same trip (the owner's copy and its public page share photos).
 */
export async function forgetTrip(hash: string): Promise<void> {
  const entries = await readPersistedQueries();
  const target = entries.find((entry) => entry.hash === hash);
  if (!target) return;
  const envelope = target.data as TripEnvelope | undefined;
  await deletePersistedQuery(hash);
  if (!envelope?.trip) return;

  const tripId = envelope.trip.id;
  const stillShown = entries.some(
    (entry) =>
      entry.hash !== hash &&
      isTripKey(entry.key) &&
      (entry.data as TripEnvelope | undefined)?.trip?.id === tripId,
  );
  for (const entry of entries) {
    if (entry.key[0] === 'trip' && entry.key[1] === tripId && !isTripKey(entry.key)) {
      await deletePersistedQuery(entry.hash);
    }
  }
  if (stillShown) return;
  await idbDelete(STORE_META, `media:${tripId}`);
  await removeMedia(mediaUrlsOf(envelope));
}

/** Wipe everything. Called on logout: offline data must not outlive a session. */
export async function clearOfflineData(): Promise<void> {
  await idbWipe();
  await clearMediaCache();
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

const MEDIA_CACHE = 'travessa-media-v1';

/**
 * Every photo and document URL a trip needs. Ordering and the same-origin rule
 * live in the contract (`tripMediaUrls`), so a future client agrees.
 *
 * On a connection the browser calls metered or slow, the photos' originals stay
 * behind: thumbnails fill the gallery, documents always come.
 */
export function mediaUrlsOf(envelope: TripEnvelope): string[] {
  const connection = (navigator as { connection?: { saveData?: boolean; effectiveType?: string } })
    .connection;
  const lean = Boolean(connection?.saveData) || /2g/.test(connection?.effectiveType ?? '');
  return tripMediaUrls(
    envelope.trip,
    typeof location === 'undefined' ? undefined : location.origin,
    { fullPhotos: !lean },
  );
}

/**
 * Pull files into the service worker's cache.
 *
 * Best-effort and sequential-ish: a trip with 400 photos should not open 400
 * connections. Failures are counted, not thrown — a missing thumbnail is not a
 * reason to refuse to keep the itinerary.
 */
export async function cacheMedia(
  urls: string[],
  onProgress?: (done: number, total: number, stored: number) => void,
): Promise<number> {
  if (typeof caches === 'undefined' || !urls.length) return 0;
  let cache: Cache;
  try {
    cache = await caches.open(MEDIA_CACHE);
  } catch {
    return 0;
  }

  let done = 0;
  let stored = 0;
  const CONCURRENCY = 4;
  const queue = [...urls];

  const worker = async () => {
    for (;;) {
      const url = queue.shift();
      if (!url) return;
      try {
        const existing = await cache.match(url);
        if (existing) stored += 1;
        else {
          const response = await fetch(url, { credentials: 'include' });
          if (response.ok) {
            await cache.put(url, response.clone());
            stored += 1;
          }
        }
      } catch {
        // Storage full or the file is gone: keep going with the rest.
      }
      done += 1;
      onProgress?.(done, urls.length, stored);
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, urls.length) }, worker));
  return stored;
}

async function removeMedia(urls: string[]): Promise<void> {
  if (typeof caches === 'undefined' || !urls.length) return;
  try {
    const cache = await caches.open(MEDIA_CACHE);
    await Promise.all(urls.map((url) => cache.delete(url)));
  } catch {
    // Nothing to do — the cache is either gone or unreachable.
  }
}

export async function clearMediaCache(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    await caches.delete(MEDIA_CACHE);
  } catch {
    // Nothing to do — the cache is either gone or unreachable.
  }
}

/**
 * Ask the browser not to evict what is kept. Without it, storage pressure (or
 * Safari's seven-day rule for sites not added to the home screen) can clear a
 * trip the day before the flight. A refusal changes nothing; asked once.
 */
let persistAsked = false;
export function requestPersistentStorage(): void {
  if (persistAsked) return;
  persistAsked = true;
  void navigator.storage?.persist?.().catch(() => undefined);
}

/** Rough size of everything stored, for the settings screen. */
export async function estimateUsage(): Promise<{ usage: number; quota: number } | null> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (!estimate) return null;
    return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
  } catch {
    return null;
  }
}
